import type { DateFilter } from '@/hooks/dashboard/useDashboardStats';
import { readDateRangeSearch, replaceDateRangeSearch } from '@/lib/date-range-search';

/**
 * Which preset a dashboard date filter counts as, and how that choice round-
 * trips through the URL. Shared by the dashboard tabs — sibling views that
 * pick a period differently read as different products.
 */

export type DashboardPeriodChoice =
  | 'today'
  | 'last_7_days'
  | 'last_30_days'
  | 'month_to_date'
  | 'year_to_date'
  | 'custom';

export const PERIODS: Array<{ value: DashboardPeriodChoice; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'year_to_date', label: 'Year to date' },
  { value: 'custom', label: 'Custom range' },
];

export function shiftDateKey(dateKey: string, days: number) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function startOfYearKey(dateKey: string) {
  return `${dateKey.slice(0, 4)}-01-01`;
}

export function startOfMonthKey(dateKey: string) {
  return `${dateKey.slice(0, 7)}-01`;
}

/** Monday of the week `dateKey` falls in. Sunday belongs to the week ending on
 *  it, not the one starting the next day. */
export function startOfWeekKey(dateKey: string) {
  const weekday = new Date(`${dateKey}T00:00:00Z`).getUTCDay();
  return shiftDateKey(dateKey, -(weekday === 0 ? 6 : weekday - 1));
}

export function dateFromKey(dateKey: string) {
  return new Date(`${dateKey}T00:00:00`);
}

export function dateKeyFromLocalDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function selectedPeriod(filter: DateFilter, todayKey: string): DashboardPeriodChoice {
  if (filter.period === 'today') return 'today';
  if (filter.period !== 'custom' || filter.endDate !== todayKey) return 'custom';
  if (filter.startDate === shiftDateKey(todayKey, -6)) return 'last_7_days';
  if (filter.startDate === shiftDateKey(todayKey, -29)) return 'last_30_days';
  if (filter.startDate === startOfMonthKey(todayKey)) return 'month_to_date';
  if (filter.startDate === startOfYearKey(todayKey)) return 'year_to_date';
  return 'custom';
}

export function periodLabel(filter: DateFilter, todayKey: string) {
  const selected = selectedPeriod(filter, todayKey);
  if (selected !== 'custom') {
    return PERIODS.find((period) => period.value === selected)?.label ?? 'Today';
  }
  if (!filter.startDate || !filter.endDate) return 'Custom range';
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  return `${formatter.format(dateFromKey(filter.startDate))} – ${formatter.format(
    dateFromKey(filter.endDate),
  )}`;
}

export function dashboardFilterFromSearch(todayKey: string): DateFilter {
  const value = readDateRangeSearch();
  if (!value) return { period: 'today' };
  if (value.preset === 'today') return { period: 'today' };
  if (value.preset === 'custom' && value.from && value.to) {
    return { period: 'custom', startDate: value.from, endDate: value.to };
  }
  const startDate = {
    today: undefined,
    // Not one of this control's own choices, but the `range` search param is
    // shared with the Reporting pages, which do offer "This week". Reading it
    // back as `today` would silently retune a period the user chose next door.
    week: startOfWeekKey(todayKey),
    '7d': shiftDateKey(todayKey, -6),
    '30d': shiftDateKey(todayKey, -29),
    mtd: startOfMonthKey(todayKey),
    ytd: startOfYearKey(todayKey),
    custom: undefined,
  }[value.preset];
  return startDate ? { period: 'custom', startDate, endDate: todayKey } : { period: 'today' };
}

export function persistDashboardFilter(filter: DateFilter, todayKey: string) {
  const selected = selectedPeriod(filter, todayKey);
  const preset = {
    today: 'today',
    last_7_days: '7d',
    last_30_days: '30d',
    month_to_date: 'mtd',
    year_to_date: 'ytd',
    custom: 'custom',
  }[selected] as 'today' | '7d' | '30d' | 'mtd' | 'ytd' | 'custom';
  replaceDateRangeSearch(
    preset === 'custom' && filter.startDate && filter.endDate
      ? { preset, from: filter.startDate, to: filter.endDate }
      : { preset },
  );
}
